// "What happened yesterday" (v3 §2.2): ONE ranked feed built from the alarm rows, the silent partners, the launches
// off pace and the movers. Ranking:
//   1 new alarms (error / warning status that was not an alarm the day before) · silent partners · launches off pace
//   2 filtering signatures (coverage down while traffic holds — whatever the sign of $)
//   3 negative movers and partner events down
//   4 partner events up, then the positive movers collapsed into one line
//   5 ongoing alarms (still in alarm, already known) collapsed into one line
//   6 recovered — one grey line
import {ALARM_STATUSES, ERROR_STATUSES, isNilValue, fmtSignedMoney, fmtMoneyShort, shortProvider} from "./format";
import {buildAttentionItems} from "./AttentionPanel";
import {buildMovedItems, isFilteringSignature} from "./moved";

export const FEED_ANCHOR_ID = "tc-feed";
export const FEED_VISIBLE = 8;

const isAlarm = (s) => ALARM_STATUSES.includes(s);
/**
 * New vs the day before: in alarm now and either not in alarm on D-2 or in a different alarm (DEAD → STOPPED is an
 * escalation the reader must see, not an "ongoing" line). The API computes `status_prev` on daily[0..n-2].
 */
export const isNewAlarm = (row) => isAlarm(row.status) && (!isAlarm(row.status_prev) || row.status_prev !== row.status);

/** `usually $22/d → $0` figures of an alarm row (7-day median when the API has one, else the range mean). */
const atRiskOf = (row) => (row._atRisk || 0);

/**
 * @param attention  alarm rows (error + warning) after the page filters
 * @param rows       every filtered row (recovered lines, quiet-tag residual)
 * @param changes / changeGroups  API movers (already restricted to the filtered rows)
 * @param silentProviders  API `silent_providers[]`
 * @param launches   API `launches[]`
 * @param compareBase  "base" | "d1" — the $ of the movers follow it
 */
export const buildFeed = ({attention = [], rows = [], changes = [], changeGroups = [], silentProviders = [], launches = [], compareBase = "base"}) => {
    const newAlarms = attention.filter(isNewAlarm);
    const ongoing = attention.filter((r) => !isNewAlarm(r));
    const alarmTags = new Set(attention.map((r) => r.tag));
    const items = [];

    // 1 · new alarms (grouped like the old attention list), silent partners, launches off pace
    buildAttentionItems(newAlarms).forEach((it) => items.push({...it, kind: it.kind === "group" ? "alarmGroup" : "alarm", rank: 1, usd: -it.atRisk, isNew: true}));
    silentProviders.forEach((sp) => items.push({
        kind: "silent", key: `silent:${sp.provider}`, provider: sp.provider, usual: Number(sp.usual_rev_d) || 0, tags: Number(sp.tags) || 0,
        lastDay: sp.last_day, expected: sp.expected_utc, rank: 1, usd: -(Number(sp.usual_rev_d) || 0), atRisk: Number(sp.usual_rev_d) || 0,
    }));
    launches.filter((l) => l.pace_ok === false && !alarmTags.has(l.tag)).forEach((l) => items.push({
        kind: "launch", key: `launch:${l.tag}`, tag: l.tag, launch: l, rank: 1, usd: -(Number(l.day1_rev) || 0) + (Number(l.last_rev) || 0), atRisk: Math.max(0, (Number(l.day1_rev) || 0) - (Number(l.last_rev) || 0)),
    }));

    // 2–4 · movers (partner events + singles) — the movers' own ranking: 0 filtering, 1 negative, 2 positive, 3 chronic
    const moved = buildMovedItems({changes, changeGroups, rows, attention, compareBase});
    const positives = [];
    moved.forEach((m) => {
        const filtering = m.kind === "event" ? (m.direction === "down" && m.metric === "coverage") : isFilteringSignature(m.c);
        const negative = (m.usd !== null && m.usd < 0) || (m.kind === "event" && m.direction === "down");
        if (filtering) items.push({...m, rank: 2});
        else if (negative) items.push({...m, rank: 3});
        else if (m.kind === "event") items.push({...m, rank: 4});
        else positives.push(m);
    });
    const posUsd = positives.reduce((s, m) => s + (m.usd || 0), 0);
    if (positives.length) items.push({kind: "positives", key: "positives", items: positives, n: positives.length, usd: posUsd, rank: 4.5});

    // 5 · ongoing alarms, one collapsed line
    if (ongoing.length) {
        const atRisk = ongoing.reduce((s, r) => s + atRiskOf(r), 0);
        items.push({kind: "ongoing", key: "ongoing", items: buildAttentionItems(ongoing), n: ongoing.length, atRisk, usd: -atRisk, rank: 5});
    }
    // 6 · recovered
    const recovered = rows.filter((r) => r.status === "RECOVERED");
    if (recovered.length) items.push({kind: "recovered", key: "recovered", rows: recovered, n: recovered.length, rank: 6, usd: 0});

    items.sort((a, b) => (a.rank - b.rank) || (Math.abs(b.usd || 0) - Math.abs(a.usd || 0)));

    const events = moved.filter((m) => m.kind === "event").length;
    const newRisk = newAlarms.reduce((s, r) => s + atRiskOf(r), 0) + silentProviders.reduce((s, sp) => s + (Number(sp.usual_rev_d) || 0), 0);
    const negatives = items.filter((it) => it.rank === 2 || it.rank === 3).length;
    return {
        items,
        digest: {
            newCount: newAlarms.length + silentProviders.length + items.filter((it) => it.kind === "launch").length,
            newRisk, events, positives: positives.length, posUsd, ongoing: ongoing.length,
            ongoingRisk: ongoing.reduce((s, r) => s + atRiskOf(r), 0), recovered: recovered.length, negatives,
        },
        /** Quiet day: nothing new broke, no partner silent, nothing filtering, no loss. */
        quiet: newAlarms.length === 0 && silentProviders.length === 0 && negatives === 0 && !items.some((it) => it.kind === "launch"),
        positives,
    };
};

/** Header digest: `3 new · $1,141/d at risk · 2 partner events · +$72 among quiet tags`. */
export const feedDigest = (d, residualUsd, nQuiet) => {
    const parts = [];
    if (d.newCount) parts.push({text: `${d.newCount} new`, strong: true});
    if (d.newRisk > 0) parts.push({text: `${fmtMoneyShort(d.newRisk)}/d at risk`, strong: true});
    else if (d.ongoingRisk > 0) parts.push({text: `${fmtMoneyShort(d.ongoingRisk)}/d still at risk`});
    if (d.events) parts.push({text: `${d.events} partner event${d.events === 1 ? "" : "s"}`});
    if (!isNilValue(residualUsd) && nQuiet > 0) parts.push({text: `${fmtSignedMoney(residualUsd)} among ${nQuiet} quiet tags`});
    return parts;
};

/** `Also · 13 off-Tiger · 27 never earned · 14 nothing sent · 8 retired · 144k searches wasted yesterday` pieces (off-Tiger tags DO earn). */
export const silentLine = (counts, wastedSent) => {
    const parts = [
        counts.NO_TIGER ? {status: "NO_TIGER", text: `${counts.NO_TIGER} off-Tiger`} : null,
        counts.NO_REVENUE ? {status: "NO_REVENUE", text: `${counts.NO_REVENUE} never earned`} : null,
        counts.NO_TRAFFIC ? {status: "NO_TRAFFIC", text: `${counts.NO_TRAFFIC} nothing sent`} : null,
        counts.RETIRED ? {status: "RETIRED", text: `${counts.RETIRED} retired`} : null,
        counts.PARTIAL ? {status: "PARTIAL", text: `${counts.PARTIAL} not reported`} : null,
    ].filter(Boolean);
    const total = parts.reduce((s, p) => s + Number(p.text.split(" ")[0]), 0);
    return {total, parts, wasted: wastedSent > 0 ? wastedSent : null};
};

export const FEED_HELP = [
    "One list of what changed yesterday, worst first: new alarms (not in alarm the day before), partners that have not reported past their usual time, launches losing coverage, then tags moving outside their usual range.",
    "DEAD = traffic in, $0 out · STOPPED = nothing in, nothing out · NO ADS = pages without ads · PRE-KILL = Google filtering a CSE account · WATCH = pays below its usual · FILTERED = partner counts far fewer searches.",
    "Movers are not alarms: coverage ↓ with traffic ↑ = partner starting to filter · pays ↓ across a partner = repricing · traffic ↓ = our side (Tiger).",
    "'usually $22/d → $30' = the tag's 7-day median vs yesterday; '(3 d)' = a thin base. $ follow the compare control.",
    "Ongoing alarms and positive movers are folded into one line each; retired tags (8+ days stopped) are out of the counts.",
];

export const shortPartner = shortProvider;
export {ERROR_STATUSES};
